import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RecaptchaVerifier } from "firebase/auth";
import { auth } from "../../firebase";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  sendOTP,
  verifyOTP,
  completeRegistration,
  clearAuthState,
} from "../../slice/auth/authSlice";
import logo from "../../assets/logo.png";

/* ─── Styles ───────────────────────────────────────────────────────────── */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');

  :root {
    --cream: #FBF7F1;
    --gold: #C9974A;
    --gold-light: #E8C07A;
    --gold-dark: #9A6E2E;
    --brown: #3D2008;
    --muted: #8A7060;
    --border: #E2D5C3;
  }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  /* ── full page ── */
  .sl-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--cream);
    position: relative;
    overflow: hidden;
    font-family: 'DM Sans', sans-serif;
  }

  /* ── scattered bg frames ── */
  .sl-bg { position: fixed; inset: 0; pointer-events: none; z-index: 0; }

  .sl-bf {
    position: absolute;
    opacity: 0;
    animation: bfFade 1.4s ease forwards, bfFloat var(--dur, 7s) ease-in-out var(--del, 0s) infinite;
  }

  @keyframes bfFade { to { opacity: 1; } }
  @keyframes bfFloat {
    0%, 100% { transform: translateY(0) rotate(var(--rot)); }
    50% { transform: translateY(-14px) rotate(var(--rot)); }
  }

  /* outer gold frame shell */
  .sl-bf-frame {
    width: 100%;
    height: 100%;
    padding: 4px;
    background: linear-gradient(145deg, #C8892A, #EDD077, #C8892A, #F5DC8A, #B8831E);
    border-radius: 6px;
    box-shadow:
      0 0 0 1px rgba(120,80,10,.18),
      0 4px 18px rgba(80,45,5,.22),
      inset 0 0 0 1px rgba(255,230,130,.35);
    display: flex;
    flex-direction: column;
    opacity: 0.72;
  }

  /* cream mat inside the gold frame */
  .sl-bf-photo {
    flex: 1;
    background: linear-gradient(145deg, #FDF3E3, #F5E8CC);
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
  }

  /* subtle inner shadow on photo area */
  .sl-bf-photo::after {
    content: '';
    position: absolute;
    inset: 0;
    box-shadow: inset 0 2px 8px rgba(120,70,10,.12);
    border-radius: 3px;
  }

  .sl-bf-emoji {
    font-size: 28px;
    filter: saturate(.7) brightness(.95);
    opacity: .7;
  }

  /* polaroid white label strip at bottom */
  .sl-bf-label {
    background: #fff;
    border-radius: 0 0 3px 3px;
    text-align: center;
    font-family: 'DM Sans', sans-serif;
    font-size: 8px;
    font-weight: 500;
    color: #A08060;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    padding: 3px 2px 2px;
    opacity: .85;
  }

  /* ── card wrapper with golden border ── */
  .sl-wrap {
    position: relative;
    z-index: 2;
    padding: 24px;
    animation: slUp .65s cubic-bezier(.22,.68,0,1.2) both;
  }

  @keyframes slUp {
    from { opacity: 0; transform: translateY(32px) scale(.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .sl-outer {
    position: relative;
    padding: 5px;
    border-radius: 20px;
    background: linear-gradient(145deg, #B8831E 0%, #EDD077 25%, #C8892A 50%, #F5DC8A 75%, #B8831E 100%);
    box-shadow:
      0 0 0 1px rgba(120,80,10,.25),
      0 12px 50px rgba(80,45,5,.22),
      0 3px 10px rgba(160,110,20,.18);
  }

  /* decorative outer ring */
  .sl-outer::after {
    content: '';
    position: absolute;
    inset: 9px;
    border: 1px solid rgba(255,235,140,.45);
    border-radius: 15px;
    pointer-events: none;
    z-index: 5;
  }

  /* corner ornaments */
  .sl-corner {
    position: absolute;
    width: 30px;
    height: 30px;
    z-index: 6;
  }
  .sl-corner svg { width: 100%; height: 100%; }
  .sl-c-tl { top: 10px; left: 10px; }
  .sl-c-tr { top: 10px; right: 10px; transform: scaleX(-1); }
  .sl-c-bl { bottom: 10px; left: 10px; transform: scaleY(-1); }
  .sl-c-br { bottom: 10px; right: 10px; transform: scale(-1,-1); }

  /* ── inner white card ── */
  .sl-card {
    background: #FFFDF9;
    border-radius: 15px;
    padding: 40px 38px 36px;
    width: 430px;
    max-width: calc(100vw - 72px);
    position: relative;
    overflow: hidden;
  }

  /* ── logo area ── */
  .sl-logo-zone {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
    margin-bottom: 8px;
  }

  .sl-logo {
    height: 45px;
    display: block;
    filter: drop-shadow(0 2px 6px rgba(160,100,20,.15));
  }

  /* ── heading ── */
  .sl-heading {
    font-family: 'DM Sans', sans-serif;
    font-size: 18px;
    font-weight: 500;
    color: var(--brown);
    text-align: center;
    margin: 14px 0 6px;
    letter-spacing: .2px;
  }

  /* ── step indicator ── */
  .sl-steps {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-bottom: 22px;
  }

  .sl-step-dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: #ddd3c3;
    transition: all .35s cubic-bezier(.34,1.56,.64,1);
  }
  .sl-step-dot.active {
    width: 24px;
    border-radius: 4px;
    background: var(--gold);
  }
  .sl-step-dot.done {
    background: var(--gold-dark);
  }

  /* step label */
  .sl-step-label {
    text-align: center;
    font-size: 11px;
    color: var(--muted);
    letter-spacing: .8px;
    text-transform: uppercase;
    margin-bottom: 18px;
    font-weight: 400;
  }

  /* ── inputs ── */
  .sl-inp {
    width: 100%;
    padding: 13px 16px;
    border: 1.5px solid var(--border);
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14.5px;
    color: var(--brown);
    background: #FDFAF5;
    outline: none;
    transition: border-color .2s, box-shadow .2s, background .2s;
    margin-bottom: 11px;
    display: block;
  }
  .sl-inp:focus {
    border-color: var(--gold);
    background: #fff;
    box-shadow: 0 0 0 3.5px rgba(201,151,74,.12);
  }
  .sl-inp::placeholder { color: #C0AEAA; }

  .sl-inp-otp {
    letter-spacing: 10px;
    font-size: 22px;
    text-align: center;
    font-weight: 500;
  }

  /* ── buttons ── */
  .sl-btn-main {
    width: 100%;
    padding: 13.5px;
    background: linear-gradient(135deg, #8B5E1A 0%, #C9974A 55%, #E8C07A 100%);
    color: #fff;
    border: none;
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 500;
    letter-spacing: .5px;
    cursor: pointer;
    box-shadow: 0 4px 18px rgba(140,90,20,.32), 0 1px 3px rgba(140,90,20,.2);
    transition: transform .15s, box-shadow .15s, opacity .15s;
    margin-top: 2px;
    position: relative;
    overflow: hidden;
  }
  .sl-btn-main::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, rgba(255,255,255,.15), transparent);
    border-radius: 10px;
  }
  .sl-btn-main:hover:not(:disabled) {
    transform: translateY(-1.5px);
    box-shadow: 0 7px 24px rgba(140,90,20,.38);
  }
  .sl-btn-main:active:not(:disabled) { transform: translateY(0); }
  .sl-btn-main:disabled { opacity: .6; cursor: not-allowed; }

  .sl-btn-outline {
    width: 100%;
    padding: 11px;
    background: transparent;
    color: var(--gold-dark);
    border: 1.5px solid var(--gold-light);
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    margin-top: 8px;
    transition: background .2s, border-color .2s;
  }
  .sl-btn-outline:hover:not(:disabled) {
    background: rgba(201,151,74,.07);
    border-color: var(--gold);
  }
  .sl-btn-outline:disabled { opacity: .5; cursor: not-allowed; }

  .sl-btn-link {
    background: none;
    border: none;
    color: #A08878;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    cursor: pointer;
    width: 100%;
    padding: 9px 0 0;
    text-align: center;
    display: block;
    text-decoration: underline;
    text-underline-offset: 3px;
    transition: color .2s;
  }
  .sl-btn-link:hover { color: var(--gold-dark); }

  /* ── alerts ── */
  .sl-alert {
    padding: 11px 14px;
    border-radius: 9px;
    font-size: 13.5px;
    margin-bottom: 14px;
    font-family: 'DM Sans', sans-serif;
    line-height: 1.5;
  }
  .sl-alert-info { background: #FFF8EC; border: 1px solid #F0D090; color: #7A5218; }
  .sl-alert-success { background: #F2FAF4; border: 1px solid #B0DDB8; color: #2A6A3A; }
  .sl-alert-danger { background: #FFF2F2; border: 1px solid #F5BABA; color: #8B2020; }

  /* ── divider ── */
  .sl-divider {
    display: flex; align-items: center; gap: 10px;
    margin: 22px 0 14px;
  }
  .sl-divider hr { flex: 1; border: none; border-top: 1px solid var(--border); }
  .sl-divider span {
    font-size: 10px; color: #C8AE90; letter-spacing: 1.5px;
    text-transform: uppercase; white-space: nowrap;
    font-family: 'DM Sans', sans-serif;
  }

  /* ── polaroid frame strip ── */
  .sl-frames {
    display: flex;
    gap: 8px;
    justify-content: center;
    align-items: flex-end;
  }

  .sl-pol {
    padding: 4px 4px 12px;
    background: #fff;
    box-shadow: 0 2px 10px rgba(80,45,5,.16), 0 1px 3px rgba(80,45,5,.1);
    flex-shrink: 0;
    transition: transform .25s cubic-bezier(.34,1.56,.64,1), box-shadow .25s;
    cursor: default;
  }
  .sl-pol:hover {
    transform: rotate(0deg) scale(1.1) translateY(-4px) !important;
    box-shadow: 0 8px 22px rgba(80,45,5,.22);
    z-index: 2;
    position: relative;
  }

  .sl-pol-img {
    background: linear-gradient(135deg, #FCE8B2, #F0C870);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
  }

  .sl-frames-caption {
    text-align: center;
    font-size: 10.5px;
    color: #C0A080;
    letter-spacing: 1.8px;
    text-transform: uppercase;
    margin-top: 11px;
    font-family: 'DM Sans', sans-serif;
  }

  /* ── spinner ── */
  .sl-spin {
    display: inline-block;
    width: 13px; height: 13px;
    border: 2px solid rgba(255,255,255,.35);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin .65s linear infinite;
    vertical-align: middle;
    margin-right: 7px;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* ── Mobile: ≤700px — full screen, no bg frames ── */
  @media (max-width: 650px) {
    .sl-bg { display: none; }

    .sl-page {
      align-items: stretch;
      justify-content: stretch;
      padding: 0;
      min-height: 100dvh;
    }

    .sl-wrap {
      padding: 0;
      width: 100%;
      min-height: 100dvh;
      display: flex;
      flex-direction: column;
      animation: none;
    }

    .sl-outer {
      flex: 1;
      display: flex;
      flex-direction: column;
      border-radius: 0;
      padding: 3px 0 0;
      box-shadow: none;
    }

    .sl-outer::after { display: none; }

    .sl-corner { display: none; }

    .sl-card {
      flex: 1;
      border-radius: 0;
      width: 100%;
      max-width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: 36px 24px 28px;
      overflow-y: auto;
    }
  }
`;

/* ─── Corner SVG ───────────────────────────────────────────────────────── */
const Corner = () => (
  <svg viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 26 Q4 4 26 4" stroke="#D4A040" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
    <path d="M2 24 Q2 2 24 2" stroke="#F5DC8A" strokeWidth=".8" fill="none" strokeLinecap="round"/>
    <path d="M6 26 Q6 6 26 6" stroke="rgba(212,160,64,.4)" strokeWidth=".6" fill="none" strokeLinecap="round"/>
    <circle cx="4" cy="26" r="2.2" fill="#C9974A"/>
    <circle cx="26" cy="4" r="2.2" fill="#C9974A"/>
    <circle cx="4" cy="4" r="1.3" fill="#F0D080" opacity=".8"/>
  </svg>
);

/* ─── Step dots ────────────────────────────────────────────────────────── */
const Steps = ({ current }) => {
  const labels = [];
  return (
    <>
      <div className="sl-steps">
        {[0, 1, 2].map(i => (
          <div key={i} className={`sl-step-dot ${i === current ? "active" : i < current ? "done" : ""}`} />
        ))}
      </div>
      <p className="sl-step-label">{labels[current]}</p>
    </>
  );
};

/* ─── Polaroid strip ───────────────────────────────────────────────────── */
const FrameStrip = () => {
  const items = [
    { emoji: "🎂", w: 46, h: 50, rot: "-5deg" },
    { emoji: "💍", w: 40, h: 54, rot: "-2deg" },
    { emoji: "🎓", w: 46, h: 46, rot: "0deg" },
    { emoji: "🌸", w: 40, h: 50, rot: "2.5deg" },
    { emoji: "🏆", w: 46, h: 52, rot: "4deg" },
  ];
  return (
    <>
      <div className="sl-divider">
        <hr /><span>Frame your moments</span><hr />
      </div>
      <div className="sl-frames">
        {items.map((f, i) => (
          <div key={i} className="sl-pol" style={{ transform: `rotate(${f.rot})` }}>
            <div className="sl-pol-img" style={{ width: f.w, height: f.h }}>
              <span>{f.emoji}</span>
            </div>
          </div>
        ))}
      </div>
      <p className="sl-frames-caption">birthday · wedding · festival · and more</p>
    </>
  );
};

/* ─── Background decorative photo frames ──────────────────────────────── */
const BgFrames = () => {
  const frames = [
    { w:90, h:110, top:"6%",   left:"3%",    rot:"-8deg",  dur:"7s",   del:"0s",   emoji:"🎂", label:"Birthday" },
    { w:72, h:88,  top:"12%",  right:"4%",   rot:"6deg",   dur:"9s",   del:"1s",   emoji:"💍", label:"Wedding" },
    { w:58, h:72,  bottom:"24%",left:"5%",   rot:"4deg",   dur:"8s",   del:"2s",   emoji:"🎓", label:"" },
    { w:80, h:98,  bottom:"8%", right:"4%",  rot:"-5deg",  dur:"10s",  del:".5s",  emoji:"🌸", label:"Festival" },
    { w:50, h:62,  top:"44%",  left:"1.5%",  rot:"11deg",  dur:"11s",  del:"1.5s", emoji:"🏆", label:"" },
    { w:62, h:76,  top:"52%",  right:"2%",   rot:"-8deg",  dur:"8.5s", del:"3s",   emoji:"🎉", label:"" },
  ];

  return (
    <div className="sl-bg">
      {frames.map((f, i) => (
        <div
          key={i}
          className="sl-bf"
          style={{
            width: f.w, height: f.h,
            top: f.top, bottom: f.bottom,
            left: f.left, right: f.right,
            "--rot": f.rot, "--dur": f.dur, "--del": f.del,
            animationDelay: f.del,
          }}
        >
          <div className="sl-bf-frame">
            <div className="sl-bf-photo">
              <span className="sl-bf-emoji">{f.emoji}</span>
            </div>
            {f.label && <div className="sl-bf-label">{f.label}</div>}
          </div>
        </div>
      ))}
    </div>
  );
};

/* ─── Main Login Component ─────────────────────────────────────────────── */
const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    loading, error, isSuccess, otpSent, otpVerified, isNewUser,
    verificationId, phoneNumber, firebaseUid, firebaseToken,
  } = useSelector((state) => state.auth);

  const [form, setForm] = useState({ phoneNumber: "", otp: "", name: "", email: "" });
  const [resendTimer, setResendTimer] = useState(0);
  const recaptchaVerifierRef = useRef(null);

  const step = otpVerified ? 2 : otpSent ? 1 : 0;

  useEffect(() => {
    if (!recaptchaVerifierRef.current) {
      try {
        window.recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
          size: "invisible",
          callback: () => {},
          "expired-callback": () => {},
        });
        recaptchaVerifierRef.current = window.recaptchaVerifier;
      } catch (err) { console.error(err); }
    }
    return () => {
      if (window.recaptchaVerifier) {
        try { window.recaptchaVerifier.clear(); } catch {}
        window.recaptchaVerifier = null;
      }
    };
  }, []);

  useEffect(() => {
    let t;
    if (resendTimer > 0) t = setInterval(() => setResendTimer(p => p - 1), 1000);
    return () => clearInterval(t);
  }, [resendTimer]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const fmt = (n) => n.startsWith("+") ? n : `+91${n}`;

  const handleSendOTP = (e) => {
    e.preventDefault();
    if (!recaptchaVerifierRef.current) return;
    dispatch(sendOTP({ phoneNumber: fmt(form.phoneNumber), recaptchaVerifier: recaptchaVerifierRef.current }));
    setResendTimer(60);
  };

  const handleResendOTP = () => {
    if (resendTimer > 0 || !recaptchaVerifierRef.current) return;
    dispatch(sendOTP({ phoneNumber: fmt(form.phoneNumber), recaptchaVerifier: recaptchaVerifierRef.current }));
    setResendTimer(60);
  };

  const handleVerifyOTP = (e) => {
    e.preventDefault();
    if (!form.otp || form.otp.length !== 6) return alert("Please enter a 6-digit OTP");
    dispatch(verifyOTP({ verificationId, otpCode: form.otp, phoneNumber }));
  };

  const handleCompleteRegistration = (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.name.trim().length < 2) return alert("Name must be at least 2 characters");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return alert("Please enter a valid email");
    dispatch(completeRegistration({ name: form.name.trim(), email: form.email.trim(), phoneNumber, firebaseUid, firebaseToken }));
  };

  useEffect(() => {
    if (isSuccess) {
      toast.success(isNewUser ? "Account created! 🎉" : "Welcome back! 🖼️");
      navigate("/");
      dispatch(clearAuthState());
    }
  }, [isSuccess, navigate, dispatch]);

  const handleBack = () => {
    dispatch(clearAuthState());
    setForm({ phoneNumber: "", otp: "", name: "", email: "" });
    setResendTimer(0);
    window.location.reload();
  };

  return (
    <>
      <style>{styles}</style>
      <div className="sl-page">
        <div id="recaptcha-container" />
        <BgFrames />

        <div className="sl-wrap">
          <div className="sl-outer">
            {/* Corner ornaments */}
            {["tl","tr","bl","br"].map(c => (
              <div key={c} className={`sl-corner sl-c-${c}`}><Corner /></div>
            ))}

            <div className="sl-card">
              {/* Logo */}
              <div className="sl-logo-zone">
                <img src={logo} alt="Shaart" className="sl-logo" />
              </div>

              {/* Steps */}
              <Steps current={step} />

              {/* Heading */}
              <h2 className="sl-heading">
                {otpVerified && isNewUser ? "Complete Registration" : "User Login"}
              </h2>

              {/* Error */}
              {error && <div className="sl-alert sl-alert-danger">⚠️ {error}</div>}

              {/* STEP 1 — Phone */}
              {!otpSent && !otpVerified && (
                <form onSubmit={handleSendOTP}>
                  <input
                    type="tel"
                    name="phoneNumber"
                    className="sl-inp"
                    placeholder="📱  Phone Number"
                    value={form.phoneNumber}
                    onChange={handleChange}
                    maxLength="10"
                    required
                  />
                  <button type="submit" className="sl-btn-main" disabled={loading}>
                    {loading ? <><span className="sl-spin"/>Sending OTP…</> : "Send OTP →"}
                  </button>
                </form>
              )}

              {/* STEP 2 — OTP */}
              {otpSent && !otpVerified && (
                <form onSubmit={handleVerifyOTP}>
                  <div className="sl-alert sl-alert-info">
                    📲 OTP sent to <strong>{phoneNumber}</strong>
                  </div>
                  <input
                    type="text"
                    name="otp"
                    className="sl-inp sl-inp-otp"
                    placeholder="— — — — — —"
                    value={form.otp}
                    onChange={handleChange}
                    maxLength="6"
                    autoFocus
                    required
                  />
                  <button type="submit" className="sl-btn-main" disabled={loading}>
                    {loading ? <><span className="sl-spin"/>Verifying…</> : "Verify OTP ✓"}
                  </button>
                  <button type="button" className="sl-btn-outline" onClick={handleResendOTP} disabled={loading || resendTimer > 0}>
                    {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : "Resend OTP"}
                  </button>
                  <button type="button" className="sl-btn-link" onClick={handleBack} disabled={loading}>
                    ← Change Phone Number
                  </button>
                </form>
              )}

              {/* STEP 3 — Registration */}
              {otpVerified && isNewUser && (
                <form onSubmit={handleCompleteRegistration}>
                  <div className="sl-alert sl-alert-success">
                    ✅ Phone verified! Complete your profile.
                  </div>
                  <input
                    type="text"
                    name="name"
                    className="sl-inp"
                    placeholder="👤  Full Name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    minLength="2"
                    autoFocus
                  />
                  <input
                    type="email"
                    name="email"
                    className="sl-inp"
                    placeholder="✉️  Email Address"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                  <button type="submit" className="sl-btn-main" disabled={loading}>
                    {loading ? <><span className="sl-spin"/>Creating Account…</> : "Complete Registration 🎉"}
                  </button>
                  <button type="button" className="sl-btn-link" onClick={handleBack} disabled={loading}>
                    ← Start Over
                  </button>
                </form>
              )}

              {/* Frame strip */}
              <FrameStrip />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;